/**
 * ThreadView — status pill + lane chip header, message bubbles with
 * SYSTEM_EVENT dividers, a CSAT thumbs prompt once resolved, and a reply
 * composer that reopens a resolved/closed request (server-side; this just
 * shows the notice).
 */

import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { useSupportWidgetStore } from '../../../stores/supportWidgetStore';
import { useCsat, useMarkSupportRead, useSupportReply, useSupportRequest } from '../../../hooks/useSupport';
import supportService from '../../../services/api/supportService';
import { LANE_LABEL, STATUS_LABEL, StatusPill, LaneChip, CenteredState } from './supportShared';
import { SupportComposer } from './SupportComposer';
import { MessageThread } from '../MessageThread';
import { LoadingSpinner } from '../../ui/LoadingSpinner';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} 0 ${({ theme }) => theme.spacing[2]};
`;

const Messages = styled.div`
  flex: 1;
  padding: 0 ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
`;

const CsatCard = styled.div`
  margin: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  text-align: center;
`;

const CsatQuestion = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const CsatButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
`;

const CsatButton = styled.button<{ $selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary.base : theme.colors.borders.light)};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.status.success.bg : theme.colors.backgrounds.white};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.status.success.text : theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const CsatDone = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// Kept the name (rather than renaming to "ResolvedNotice") since it still
// marks the same resolved-thread footer slot; only its copy changed for
// ADO #542 item 17.
const ReopenNotice = styled.div`
  margin: 0 ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.info.text};
`;

const ComposerBox = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;


export interface ThreadViewProps {
  requestId: string;
}

export function ThreadView({ requestId }: ThreadViewProps) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: request, isLoading } = useSupportRequest(requestId);
  const setThreadHeader = useSupportWidgetStore((s) => s.setThreadHeader);
  const markRead = useMarkSupportRead();
  const reply = useSupportReply(requestId);
  const csat = useCsat(requestId);
  const [csatSelected, setCsatSelected] = useState<number | null>(null);

  useEffect(() => {
    if (requestId) markRead.mutate(requestId);
    // Only re-run when switching threads — marking read is a one-shot side effect per view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  // Lifts subject + reference into supportWidgetStore so SupportWidget's panel
  // header can show them without a second fetch (ADO #542 items 9/10).
  useEffect(() => {
    if (request) setThreadHeader({ subject: request.subject, reference: request.reference });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id, request?.subject, request?.reference]);

  // CLOSED was removed (ADO #542 item 16) — RESOLVED is the only terminal state.
  const wasResolved = request?.status === 'RESOLVED';
  const canRateCsat = wasResolved && request?.csatRating == null;

  const messages = useMemo(() => request?.messages ?? [], [request]);

  if (isLoading || !request) {
    return (
      <CenteredState>
        <LoadingSpinner size="md" />
      </CenteredState>
    );
  }

  return (
    <Wrapper>
      <MetaRow>
        <StatusPill $status={request.status}>{STATUS_LABEL[request.status]}</StatusPill>
        <LaneChip>{LANE_LABEL[request.lane]}</LaneChip>
      </MetaRow>

      <Messages>
        <MessageThread
          messages={messages}
          currentUserId={currentUserId}
          otherPartyLabel={LANE_LABEL[request.lane]}
          onDownloadAttachment={supportService.downloadAttachment}
        />

        {wasResolved && (
          <CsatCard>
            {canRateCsat ? (
              <>
                <CsatQuestion>Was this resolved to your satisfaction?</CsatQuestion>
                <CsatButtons>
                  <CsatButton
                    type="button"
                    $selected={csatSelected === 5}
                    aria-label="Satisfied"
                    onClick={() => {
                      setCsatSelected(5);
                      csat.mutate(5);
                    }}
                  >
                    <ThumbsUp size={16} />
                  </CsatButton>
                  <CsatButton
                    type="button"
                    $selected={csatSelected === 1}
                    aria-label="Not satisfied"
                    onClick={() => {
                      setCsatSelected(1);
                      csat.mutate(1);
                    }}
                  >
                    <ThumbsDown size={16} />
                  </CsatButton>
                </CsatButtons>
              </>
            ) : (
              <CsatDone>Thanks for your feedback.</CsatDone>
            )}
          </CsatCard>
        )}

        {/* Reopening is no longer automatic on reply (ADO #542 items 16/17) —
            an admin now decides whether to reopen a resolved request. */}
        {wasResolved && (
          <ReopenNotice>
            Your reply will be delivered. An admin will decide whether to reopen this request.
          </ReopenNotice>
        )}
      </Messages>

      <ComposerBox>
        <SupportComposer
          placeholder={`Reply to ${LANE_LABEL[request.lane]}…`}
          isSending={reply.isPending}
          onSend={async ({ body, attachments }) => {
            await reply.mutateAsync({ body, attachments });
          }}
        />
      </ComposerBox>
    </Wrapper>
  );
}

export default ThreadView;
